import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import React from 'react'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useTypedSelector } from 'src/State/RootReducer'

import { useNavigate } from 'react-router-dom'
import { Box } from '@mui/system'
import { makeStyles } from 'tss-react/mui'
import { isFlowTheme } from 'src/Libs/utils'
import { ApiResult, usePortalMutation } from 'src/Libs/api'

const useStyles = makeStyles()(theme => ({
  dialogPaper: {
    minHeight: '80vh',
  },
  dialogContent: {
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      padding: 0,
    },
  },
}))

export interface DeleteProfileSurveyDialogProps {
  onClose: () => void
  place: number
}

/**
 * Dialogs which asks the user to confirm that his/her accounts should be deleted
 */
const DeleteProfileSurveyDialog: React.VFC<DeleteProfileSurveyDialogProps> = ({ onClose, place }) => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()
  const userId = useTypedSelector(state => state.user.userData?.ID)
  const { classes } = useStyles()
  const navigate = useNavigate()

  const primaryAction = async (): Promise<void> => {
    if (isFlowTheme) {
      const url = t('user.userProfile.dialog.delete-url')
      const sanitizedUrl = url.replaceAll(/[\u200B-\u200D\uFEFF]/g, '').trim()
      window.open(sanitizedUrl, '_self')
    } else {
      deleteUserAccount().then(() => navigate('/logout'))
    }
  }

  const { mutateAsync: deleteUserAccount } = usePortalMutation({
    mutationFn: async () => {
      await authFetch<void>({
        path: `users/${userId}`,
        method: 'DELETE',
      })
      return new ApiResult<void>()
    },
    onError: error => {
      console.error(error)
    },
  })

  return (
    <Dialog
      title={t('user.userProfile.dialog.title')}
      actions={{
        primary: {
          name: t('user.userProfile.dialog.delete-user-account'),
          onClick: primaryAction,
        },
        secondary: {
          name: t('common.cancel'),
          onClick: onClose,
        },
      }}
      onClose={onClose}
      componentClasses={{ paper: classes.dialogPaper }}
      contentClassName={classes.dialogContent}
    >
      <Box
        id={'delete-user-survey'}
        component={'iframe'}
        src={t('user.user-profile.dialog.delete-user.survey-link')}
        sx={theme => ({
          minWidth: `calc(100% - ${theme.spacing(4)})`,
          maxWidth: `calc(100% - ${theme.spacing(4)})`,
          height: `calc(100% - ${theme.spacing(4)})`,
          border: 'none',
          position: 'absolute',
          [theme.breakpoints.down('sm')]: {
            minWidth: '100%',
            maxWidth: '100%',
            height: '100%',
          },
        })}
      />
    </Dialog>
  )
}

export default DeleteProfileSurveyDialog
